/**
 * 시스템 설정(EA-42) — 탭 「설정」·「약관」·「지갑거래서명약관」·「고객센터 안내」·「거래 자료 메일」·「자동 메일」·
 * 「보증금 단계」·「서버 점검」(2026-10-02 부터 화면 이름은 영어 — 01 14절), 설정 값 바꾸기(EA-51), 약관 판 보기(EA-52), 약관 새 판(EA-53).
 * 지갑거래서명약관(EA-65~EA-67)은 `WalletTradeTerms.tsx`, 고객센터 안내(EA-70~EA-72)는
 * `CustomerCenterGuide.tsx`, 거래 자료 메일(EA-73~EA-75)은 `MaterialMailTemplate.tsx`, 보증금 단계
 * (EA-60~EA-62)는 `Tiers.tsx`, 서버 점검(EA-68·EA-69)은 `Maintenance.tsx`.
 *
 * 2026-09-30 고침(기획팀 답·사장님 확인 — `05_확정요구사항.md` 2-5):
 * - R-XAD-04: 사용자 화면 거래 내역 조회 기간 `trade.history_months` 는 1~6개월만(원문 12 '최대 6개월',
 *   확인 42 Q-61 '관리자는 그 안에서 줄이기만', 확인 43 처음 3개월). 관리자 화면은 기간 제한 없음(확인 43).
 * - R-XAD-05: 약관 탭에 「파트너 약관」·「철회 약관」(확인 36 — 철회 신청 때 보이는 약관을 여기서 관리).
 * - R-XAD-01: 「보증금 단계」 탭(원문 2, 확인 30·31).
 * - R-XAD-07: 가스 설정은 두지 않는다(확인 24 — 재단 관리자가 체인에서 정함). 서버가 주는 설정만 보인다.
 *
 * 2026-09-30 거래 흐름 04 3-3: 「지갑거래서명약관」 탭(사장님 「…/settings 이페이지에 메뉴만들어서
 * 지갑거래서명약관으로 메뉴 만들어서 관리해야지」) — 키는 약관 탭과 같은 `terms.view`(계약 XM-100~XM-103).
 *
 * 2026-10-01 거래 흐름 04 시험: 「서버 점검」 탭(사장님 「거래소 관리자에 서버 점검 버튼만들어서 서버 점검
 * 상태를 만들어서 테스트 하면되잖아」) — 키는 설정과 같은 `settings.view`·`settings.edit`(계약 XM-104·XM-105).
 *
 * 2026-10-01 거래 흐름 04 8단계: 「고객센터 안내」·「거래 자료 메일」 탭(사장님 「거래소 관리자 약관처럼
 * 거래소관리자에서 관리 할 수 있도록 만들어라」, 「시작해」) — 지갑거래서명약관 다음 자리, 키는 약관 키
 * 그대로 `terms.view`·`terms.edit`(계약 XM-106~XM-113).
 *
 * 레거시가 비워 둔 자리(`/settings` pending)에 만든다. 확정: 거래소 01 9-1 21번(2026-09-24)
 * '3초는 관리자 설정값(6-24)', 22번(2026-09-23) '관리자에서 바꿀 수 있어야 한다(설정값, 6-24)',
 * 03 11번(2026-09-24) '기획팀이 약관 본문을 주면 거래소관리자에서 DB 의 약관을 고쳐 적용한다'.
 *
 * 계약: XM-83(목록)·XM-84(한 건)·XM-85(바꾸기)·XM-86(약관 판)·XM-87(시행 중)·XM-88(한 판)·XM-89(새 판).
 *
 * ⚠ **돈 기준일 수 있다.** 주문 최소·최대, 거래 범위, 사용료는 금액 설정이다. 값은 서버가
 * 저장하는 **문자열 그대로** 주고받고(USDE 는 내부 단위 정수), 화면은 float 을 쓰지 않는다
 * (`parseUsdeInput` — 시험 M-08). 「보낼 값」 줄에 서버로 가는 문자열을 그대로 보인다.
 *
 * ⚠ **ENUM 설정은 보기만 한다**(명세 7절 C-11). 서버는 ENUM 마다 구현한 값만 쓰는데 고를 수
 * 있는 값 목록이 계약에 없다 — 화면에서 바꿔도 동작이 안 바뀔 수 있다.
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createPartnerTerms, getEffectivePartnerTerms, getPartnerTerms, getSetting, listPartnerTerms,
  listSettings, putSetting,
} from '@/api/admin'
import { ApiError } from '@/api/errors'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import {
  AdminRef, Badge, Button, Card, Code, Field, Labeled, Maybe, Notice, Row, Tabs, TD, TextArea, TH,
  Time,
} from '@/components/ui'
import { Confirm, submitWrite, useWrite, ViewDialog } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { formatBp, formatUsde, parseUsdeInput, usde } from '@/format'
import { effectiveMillis, LangText, Plain } from '@/components/Versioned'
import { DateTimeField } from '@/components/DateFields'
import {
  SETTING_TYPE_LABEL, TERMS_KIND_LABEL, type PartnerTerms, type Setting, type TermsKind,
} from '@/api/types'
import { TiersTab } from './Tiers'
import { WALLET_TRADE_TERMS, WalletTradeTermsTab } from './WalletTradeTerms'
import { CUSTOMER_CENTER_GUIDE, CustomerCenterGuideTab } from './CustomerCenterGuide'
import { MATERIAL_MAIL, MaterialMailTemplateTab } from './MaterialMailTemplate'
import { ACCOUNT_MAIL, AccountMailTemplatesTab } from './AccountMailTemplates'
import { MAINTENANCE, MaintenanceTab } from './Maintenance'

/* ── 값 읽기 ─────────────────────────────────────────────────────── */

/**
 * 키마다 더 좁은 범위(서버 검사와 같게). 형식 검사(INTEGER 등) 뒤에 본다.
 *
 * - `trade.history_months`: 1~6(api-XB XM-85 — 6 넘으면 400). 근거 원문 12 '거래내역 조회 범위는
 *   최대 6개월 … 관리자에서 조정 가능', 확인 42(Q-61), 확인 43(처음 3개월).
 */
export const SETTING_BOUNDS: Record<string, { min: number; max: number; unit: string; note: string }> = {
  'trade.history_months': {
    min: 1, max: 6, unit: ' months',
    note: 'The trade history period members and masters see in the app. It can only be set within 6 months. The admin screens have no period limit.',
  },
  // 2026-10-12 — 사장님 2026-10-11 「과금구간은 100건당 10usde」 「거래소 관리자에서 수정할수 있도록」(거래소백엔드 V542 ·
  // `TradeFeeRates`): 1 ~ 1,000,000 건(서버도 400 — 범위는 제안, 사장님 확인 전). 바꾸면 다음 차감일부터.
  'fee.trade_block_size': {
    min: 1, max: 1_000_000, unit: ' trades',
    note: 'Monthly fee, trade-count part: every started block of this many completed trades of the master\'s members costs the block fee (fee.trade_block_fee). From 1 to 1,000,000. A change counts from the next deduction day.',
  },
}

/**
 * USDE 설정마다 범위(내부 단위 문자열, 서버 검사와 같게).
 *
 * - `fee.trade_block_fee`: 0 ~ 10,000 USDE(거래소백엔드 `TradeFeeRates` — 400; 범위는 제안, 사장님 확인 전). 바꾸면 다음
 *   차감일부터(그 전에 시작한 달은 옛 값).
 */
export const USDE_BOUNDS: Record<string, { min: string; max: string; note: string }> = {
  'fee.trade_block_fee': {
    min: '0', max: '1000000',
    note: 'Monthly fee, trade-count part: the fee of one started block of fee.trade_block_size completed trades. From 0 to 10,000 USDE. A change counts from the next deduction day.',
  },
}

/**
 * 정할 것 38(사장님 38번 「1」 — 위쪽 끝은 두지 않음(지금 최대값 그대로), 0·음수 막음(0 이 뜻이 있는 값은 빼고), 차례 검사, 로그인
 * 아이피 제한 1 이상): 서버(계약 XM-85 2026-10-13)와 같은 검사·같은 글을 칸 옆에. 숫자 설정(INTEGER·USDE·BP)만.
 *
 * 0 이 뜻이 있는 넷(서버와 같음): `trade.call_delay_seconds`(0 = 기다리지 않음), `fee.trade_block_fee`(0 = 거래 횟수 수수료
 * 없음), `collateral.penalty_bp`(0 = 위약금 없음), `collateral.floor_bp`(0 = 정지 선 없음).
 */
export const ZERO_ALLOWED: ReadonlySet<string> = new Set([
  'trade.call_delay_seconds', 'fee.trade_block_fee', 'collateral.penalty_bp', 'collateral.floor_bp',
])

const NUMERIC_TYPES: ReadonlySet<string> = new Set(['INTEGER', 'USDE', 'BP'])

/** 차례 검사의 세 키 — 경고 1 < 경고 2 < 정지 선(10000 − 정지 바닥, BP). */
const WARNING1 = 'collateral.warning1_bp'
const WARNING2 = 'collateral.warning2_bp'
const FLOOR = 'collateral.floor_bp'

/** 다른 설정의 지금 값(XM-83 목록). 모르면 undefined, 미정이면 null. */
export type CurrentValue = (key: string) => string | null | undefined

/** 형식을 지난 숫자 값에 서버와 같은 0·음수·차례 검사(정할 것 38). 통과면 null. */
function valueRuleError(type: string, value: string, key: string | undefined, current: CurrentValue | undefined): string | null {
  if (!NUMERIC_TYPES.has(type)) return null
  // 수로 본다(서버처럼) — '-0'·'000' 은 0.
  if (/^-0*[1-9]/.test(value)) return 'The value cannot be negative.'
  if (/^-?0+$/.test(value) && !(key !== undefined && ZERO_ALLOWED.has(key))) return 'The value must be larger than 0.'
  if (key !== WARNING1 && key !== WARNING2 && key !== FLOOR) return null
  // 바꾸려는 값과 나머지 둘의 지금 값으로 차례 전체를 본다(서버 — 「어느 하나를 바꿔 어긋나면 400」). 미정·모르는 짝과는 견주지 않는다.
  const at = (k: string): number | null => {
    const v = k === key ? value : current?.(k)
    return v === null || v === undefined || !/^\d{1,5}$/.test(v) ? null : Number(v)
  }
  const w1 = at(WARNING1)
  const w2 = at(WARNING2)
  const floor = at(FLOOR)
  if (w1 !== null && w2 !== null && !(w1 < w2)) return 'Warning level 1 must be lower than warning level 2.'
  if (w2 !== null && floor !== null && !(w2 < 10000 - floor)) {
    return 'Warning level 2 must be lower than the suspension line (100% − the suspension floor).'
  }
  return null
}

/** 거래 범위 JSON(`[{code,min,max}]`, 내부 단위)이면 범위들, 아니면 null. */
export function tradeRanges(value: string): { code: string; min: string; max: string }[] | null {
  let parsed: unknown
  try { parsed = JSON.parse(value) } catch { return null }
  if (!Array.isArray(parsed) || parsed.length === 0) return null
  const out: { code: string; min: string; max: string }[] = []
  for (const r of parsed) {
    if (typeof r !== 'object' || r === null) return null
    const { code, min, max } = r as Record<string, unknown>
    if (typeof code !== 'string') return null
    if (typeof min !== 'string' && typeof min !== 'number') return null
    if (typeof max !== 'string' && typeof max !== 'number') return null
    out.push({ code, min: String(min), max: String(max) })
  }
  return out
}

/** 범위마다 「코드 최소~최대 USDE」. */
function RangesPreview({ value }: { value: string }) {
  const ranges = tradeRanges(value)
  if (!ranges) return null
  return (
    <ul className="text-xs">
      {ranges.map((r) => (
        <li key={r.code} className="font-mono">
          {r.code} {formatUsde(r.min)}~{formatUsde(r.max)} USDE
        </li>
      ))}
    </ul>
  )
}

/** 설정 값을 형식별로(명세 EA-42): USDE 금액, BP %, 거래 범위, 그 밖은 원문, null 은 「미정」. */
export function SettingValue({ setting }: { setting: Setting }) {
  const v = setting.value
  if (v === null) return <span className="text-slate-400">Undecided</span>
  const bound = SETTING_BOUNDS[setting.key]
  if (bound) return <span className="font-mono tabular-nums">{v}{bound.unit}</span>
  switch (setting.type) {
    case 'USDE': return <span className="font-mono tabular-nums">{usde(v)}</span>
    case 'BP': return <span className="font-mono tabular-nums">{formatBp(v)}</span>
    case 'JSON': return tradeRanges(v) ? <RangesPreview value={v} /> : <code className="break-all font-mono text-xs">{v}</code>
    default: return <span className="break-all">{v}</span>
  }
}

/* ── EA-42 시스템 설정 화면 · 탭 ─────────────────────────────────── */

type Tab =
  | 'settings' | 'terms' | 'walletTradeTerms' | 'customerCenter' | 'materialMail' | 'accountMail' | 'tiers' | 'maintenance'

export function SettingsPage() {
  const { has } = useSession()
  const tabs: { kind: Tab; label: string }[] = []
  if (has('settings.view')) tabs.push({ kind: 'settings', label: 'Settings' })
  if (has('terms.view')) tabs.push({ kind: 'terms', label: 'Terms' })
  // 지갑거래서명약관(거래 흐름 04 3-3) — 키는 약관 키 그대로(계약 XM-100~XM-102 `terms.view`).
  if (has('terms.view')) tabs.push({ kind: 'walletTradeTerms', label: WALLET_TRADE_TERMS })
  // 고객센터 안내·거래 자료 메일(거래 흐름 04 8단계) — 키는 약관 키 그대로(계약 XM-106·XM-110 `terms.view`).
  if (has('terms.view')) tabs.push({ kind: 'customerCenter', label: CUSTOMER_CENTER_GUIDE })
  if (has('terms.view')) tabs.push({ kind: 'materialMail', label: MATERIAL_MAIL })
  // 자동 메일(2026-10-02 사장님 「1」) — 다른 메일 틀 다음, 키는 약관 키 그대로(계약 XM-119~XM-121 `terms.view`).
  if (has('terms.view')) tabs.push({ kind: 'accountMail', label: ACCOUNT_MAIL })
  // 보증금 단계(R-XAD-01) — 키는 api-XB XM-93 `settings.view`.
  if (has('settings.view')) tabs.push({ kind: 'tiers', label: 'Collateral tiers' })
  // 서버 점검(2026-10-01) — 키는 계약 XM-104 `settings.view`(켜기·끄기는 XM-105 `settings.edit`).
  if (has('settings.view')) tabs.push({ kind: 'maintenance', label: MAINTENANCE })
  const [active, setActive] = useState<Tab>(tabs[0]?.kind ?? 'settings')

  return (
    <Card title="System settings">
      <p className="mb-4 text-xs text-slate-500">
        Server settings, partner and withdrawal terms, wallet trade signing terms, customer center guide, trade material
        mail, automatic mail, the collateral tier table and server maintenance. Changes are recorded in the audit log
        with the values before and after.
      </p>
      <Tabs tabs={tabs} active={active} onSelect={setActive} />
      {active === 'settings' && <SettingsTab />}
      {active === 'terms' && <TermsTab />}
      {active === 'walletTradeTerms' && <WalletTradeTermsTab />}
      {active === 'customerCenter' && <CustomerCenterGuideTab />}
      {active === 'materialMail' && <MaterialMailTemplateTab />}
      {active === 'accountMail' && <AccountMailTemplatesTab />}
      {active === 'tiers' && <TiersTab />}
      {active === 'maintenance' && <MaintenanceTab />}
    </Card>
  )
}

/* ── 설정 탭 (XM-83) ─────────────────────────────────────────────── */

function SettingsTab() {
  const { has } = useSession()
  const q = useQuery({ queryKey: ['settings'], queryFn: listSettings })
  const [editing, setEditing] = useState<Setting | null>(null)
  const rows = q.data ?? []
  const canEdit = has('settings.edit')

  return (
    <>
      <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
        emptyText="There are no settings.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Key</th><th className={TH}>Value</th><th className={TH}>Type</th>
            <th className={TH}>Description</th><th className={TH}>Source</th><th className={TH}>Changed at</th>
            <th className={TH}>Changed by</th>{canEdit && <th className={TH}>Actions</th>}
          </tr></thead>
          <tbody>
            {/* 받은 순서대로(명세 EA-42). */}
            {rows.map((s) => (
              <tr key={s.key}>
                <td className={TD}><Code>{s.key}</Code></td>
                <td className={TD}><SettingValue setting={s} /></td>
                <td className={TD}><Labeled labels={SETTING_TYPE_LABEL} code={s.type} /></td>
                <td className={TD}><Maybe value={s.description} /></td>
                <td className={TD}><Maybe value={s.source} /></td>
                <td className={TD}><Time ms={s.updatedAt} /></td>
                <td className={TD}><AdminRef adminId={s.updatedByAdminId} /></td>
                {canEdit && (
                  <td className={TD}>
                    {/* ENUM 은 보기만(7절 C-11). */}
                    {s.type !== 'ENUM' && (
                      <Button tone="plain" onClick={() => setEditing(s)}>Change</Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      {editing && <SettingDialog settingKey={editing.key} fallback={editing} onClose={() => setEditing(null)} />}
    </>
  )
}

/**
 * 입력 글 → 서버로 보낼 문자열(형식별 검사). 막히면 `error` 에 까닭.
 *
 * ⚠ 서버 검사(`JdbcAppSettings.normalize`)와 같은 모양만 보낸다 — INTEGER `-?[0-9]{1,18}`,
 * BP 0~10000, USDE 내부 단위 `[0-9]{1,18}`, BOOLEAN `true`·`false`, JSON 은 JSON. 그 뒤 숫자 설정은 0·음수·차례
 * (정할 것 38 — [valueRuleError]). `current` 는 차례 검사가 볼 다른 설정의 지금 값.
 */
export function toSettingValue(
  type: string, input: string, key?: string, current?: CurrentValue,
): { value: string | null; error: string | null } {
  const r = formatValue(type, input, key)
  if (r.value === null) return r
  const rule = valueRuleError(type, r.value, key, current)
  return rule === null ? r : { value: null, error: rule }
}

/** 형식(과 키마다의 범위)만 — [toSettingValue] 의 앞 단계. */
function formatValue(type: string, input: string, key?: string): { value: string | null; error: string | null } {
  const s = input.trim()
  const bound = key === undefined ? undefined : SETTING_BOUNDS[key]
  if (bound) {
    // ⚠ 범위가 있는 키는 정수만, 범위 안만(서버도 400 으로 막는다).
    const ok = /^\d{1,7}$/.test(s) && Number(s) >= bound.min && Number(s) <= bound.max
    return ok
      ? { value: String(Number(s)), error: null }
      : { value: null, error: `Enter a whole number from ${bound.min.toLocaleString('en-US')} to ${bound.max.toLocaleString('en-US')}${bound.unit}.` }
  }
  const usdeBound = key === undefined ? undefined : USDE_BOUNDS[key]
  if (usdeBound && type === 'USDE') {
    // ⚠ 범위가 있는 USDE 키는 범위 안만(서버도 400) — 내부 단위 정수 문자열을 BigInt 로 견준다(float 없음).
    const units = parseUsdeInput(s)
    const ok = units !== null && units.length <= 18 && BigInt(units) >= BigInt(usdeBound.min) && BigInt(units) <= BigInt(usdeBound.max)
    return ok
      ? { value: units, error: null }
      : { value: null, error: `Enter a USDE amount from ${formatUsde(usdeBound.min)} to ${formatUsde(usdeBound.max)}.` }
  }
  switch (type) {
    case 'INTEGER':
      return /^-?\d{1,18}$/.test(s) ? { value: s, error: null } : { value: null, error: 'Enter a whole number.' }
    case 'BP': {
      if (!/^\d{1,5}$/.test(s)) return { value: null, error: 'Enter a whole number from 0 to 10000 (in 1/100 %).' }
      const n = Number(s)
      return n <= 10000 ? { value: String(n), error: null } : { value: null, error: 'Enter a whole number from 0 to 10000 (in 1/100 %).' }
    }
    case 'USDE': {
      const units = parseUsdeInput(s)
      if (units === null) return { value: null, error: 'Enter a USDE amount with up to two decimal places.' }
      return units.length > 18 ? { value: null, error: 'The amount is too large.' } : { value: units, error: null }
    }
    case 'BOOLEAN':
      return s === 'true' || s === 'false' ? { value: s, error: null } : { value: null, error: 'Choose On or Off.' }
    case 'JSON':
      try { JSON.parse(s); return { value: s, error: null } } catch { return { value: null, error: 'This is not valid JSON.' } }
    case 'TEXT':
      // 서버도 앞뒤 공백을 잘라 저장한다(`JdbcAppSettings.update`).
      return { value: s, error: null }
    default:
      return { value: null, error: 'This type cannot be changed here.' }
  }
}

/** 지금 값 → 입력 칸의 처음 글. */
function initialInput(s: Setting): string {
  if (s.value === null) return ''
  if (s.type === 'USDE') return formatUsde(s.value) ?? s.value
  return s.value
}

/** EA-51 — 설정 값 바꾸기(XM-84 → XM-85). */
function SettingDialog({ settingKey, fallback, onClose }: {
  settingKey: string
  fallback: Setting
  onClose: () => void
}) {
  const qc = useQueryClient()
  const w = useWrite()
  const q = useQuery({ queryKey: ['setting', settingKey], queryFn: () => getSetting(settingKey), staleTime: 0 })
  // 차례 검사(정할 것 38)가 볼 다른 설정의 지금 값 — 설정 탭이 이미 읽은 XM-83 목록(창을 열 때 다시 부르지 않는다 —
  // 바꾸면 목록을 다시 읽는다, 마지막 판단은 서버).
  const all = useQuery({ queryKey: ['settings'], queryFn: listSettings, staleTime: Infinity })
  const s = q.data ?? fallback
  const [input, setInput] = useState<string | null>(null)
  const text = input ?? initialInput(s)
  const current: CurrentValue = (k) => all.data?.find((x) => x.key === k)?.value
  const { value, error } = toSettingValue(s.type, text, s.key, current)
  const bound = SETTING_BOUNDS[s.key]
  const usdeBound = USDE_BOUNDS[s.key]
  const changed = value !== null && value !== s.value

  async function submit() {
    if (value === null) return
    const send = async () => {
      await putSetting(settingKey, value)
      setFlash({ tone: 'ok', text: `Changed ${settingKey}.` })
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['settings'] }),
        qc.invalidateQueries({ queryKey: ['setting', settingKey] }),
      ])
    }
    await submitWrite(w, 'Change setting', send, onClose)
  }

  // ⚠ 설정 값은 이 창에서 다시 바꿀 수 있다(시험 T-39·T-46 도 되돌린다) — 「되돌릴 수 없습니다」를
  // 붙이지 않는다(밤 시험 X-04).
  return (
    <Confirm open wide title={`Setting ${settingKey}`} action="Apply" danger irreversible={false}
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={q.isPending || !changed}>
      <div className="flex flex-col gap-3">
        <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
          <Row label="Description"><Maybe value={s.description} /></Row>
          <Row label="Source"><Maybe value={s.source} /></Row>
          <Row label="Type"><Labeled labels={SETTING_TYPE_LABEL} code={s.type} /></Row>
          <Row label="Current value"><SettingValue setting={s} /></Row>
        </dl>

        {s.type === 'BOOLEAN' ? (
          <fieldset className="flex gap-4">
            {(['true', 'false'] as const).map((b) => (
              <label key={b} className="flex items-center gap-2">
                <input type="radio" name="setting-bool" checked={text === b} onChange={() => setInput(b)} />
                <span>{b === 'true' ? 'On' : 'Off'}</span>
              </label>
            ))}
          </fieldset>
        ) : s.type === 'JSON' || s.type === 'TEXT' ? (
          <TextArea label="New value" value={text} rows={s.type === 'JSON' ? 8 : 3}
            onChange={(e) => setInput(e.target.value)} />
        ) : (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">New value</span>
            <span className="flex items-center gap-2">
              <input value={text} aria-label="New value"
                onChange={(e) => setInput(e.target.value)}
                className="rounded border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800" />
              {s.type === 'USDE' && <span>USDE</span>}
              {bound && <span>{bound.unit}</span>}
              {s.type === 'BP' && value !== null && <span>{formatBp(value)}</span>}
            </span>
          </label>
        )}

        {error && (text.trim() !== '' || settingKey === 'fee.trade_block_size') &&
          <p className="text-xs text-red-700 dark:text-red-400">{error}</p>}
        {bound && <p className="text-xs text-slate-500">{bound.note}</p>}
        {usdeBound && <p className="text-xs text-slate-500">{usdeBound.note}</p>}
        {s.type === 'JSON' && value !== null && <RangesPreview value={value} />}

        <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
          <Row label="Value to send"><code className="break-all font-mono text-xs">{value ?? '—'}</code></Row>
        </dl>

        <p className="text-xs text-slate-500">
          This value may be used in trade and collateral calculations. Changes are recorded in the audit log with
          the values before and after, and they stay after the server restarts.
        </p>
      </div>
    </Confirm>
  )
}

/* ── 약관 탭 (XM-86 · XM-87) ─────────────────────────────────────── */

/** 시행 중인 판 — 없으면(404) null(배지 없이 목록만 — 명세 EA-42). */
async function effectiveOrNull(kind: TermsKind): Promise<PartnerTerms | null> {
  try {
    return await getEffectivePartnerTerms(kind)
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null
    throw err
  }
}

/** 약관 종류마다 어디에 보이는지(새 판 창·탭 안내). */
const TERMS_WHERE: Record<TermsKind, string> = {
  PARTNER: 'From the effective time, the app shows this version in the partner application.',
  WITHDRAWAL: 'From the effective time, the app shows this version in the terms notice of the withdrawal request.',
}

const TERMS_KINDS: readonly TermsKind[] = ['PARTNER', 'WITHDRAWAL']

/*
 * 시연용 한국어 판(2026-10-12 — 사장님 2026-10-10 「영어로만 만들되 시연때는 한글 써야되니까 한번에 제거할수있도록
 * 한글작업되야해」, 메인 세션 「응 그럼 1234 시작해」 4번: 약관마다 영어 본문과 한국어 본문을 함께 넣고, 한국어는 시연 때
 * 한국어 폰에서만 보인다). 거래소백엔드 V544 — XM-89 `titleKo`·`bodyKo`(둘 다 또는 둘 다 없음), 앱(XA-41·XA-67)은
 * 서버 스위치 `EXCHANGE_APP_KOREAN` 이 켜져 있고 앱이 한국어일 때만 한국어를 받는다. 영어 칸은 그대로 영어만.
 */

/** 한국어 판이 어디에 보이는지(새 판 창 안내). */
const KOREAN_NOTE = "Korean (demo): the exchange app shows the Korean title and body only while the exchange server's"
  + ' Korean switch (EXCHANGE_APP_KOREAN) is on and the phone is set to Korean. Otherwise, and in the release, it shows'
  + ' the English. Write both Korean fields or leave both empty. The English fields take English only.'

/** 한국어 칸 하나만 채웠을 때 — 서버 400 `bad_request` 의 글과 같다. */
const KOREAN_PAIR = 'Write both the Korean title and the Korean body, or leave both empty.'

/** 한국어 판이 없을 때 「한국어」 칸에 보이는 글. */
const NO_KOREAN = 'No Korean text. The app in Korean shows the English.'

/** 한국어 판이 있는지 — 제목·본문 둘 다(서버 `PartnerTerms.hasKorean` 과 같게). */
function hasKorean(titleKo: string | null | undefined, bodyKo: string | null | undefined): boolean {
  return (titleKo ?? '').trim() !== '' && (bodyKo ?? '').trim() !== ''
}

/** 한 언어의 제목·본문 — 앱처럼 제목 한 줄과 본문(글 그대로, HTML 로 그리지 않는다 — EA-52). */
function TermsText({ lang, title, body }: { lang: string; title: string; body: string }) {
  return (
    <LangText lang={lang}>
      <p className="mb-2 font-medium">{title}</p>
      <Plain text={body} />
    </LangText>
  )
}

/** 영어 판과 한국어 판을 나란히(보기 창·새 판 창의 미리보기). 한국어가 없으면 영어를 보인다는 안내. */
function TermsSideBySide({ english, korean, title, body, titleKo, bodyKo }: {
  english: string
  korean: string
  title: string
  body: string
  titleKo: string | null | undefined
  bodyKo: string | null | undefined
}) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <TermsText lang={english} title={title} body={body} />
      {hasKorean(titleKo, bodyKo)
        ? <TermsText lang={korean} title={(titleKo ?? '').trim()} body={bodyKo ?? ''} />
        : <LangText lang={korean}><p className="text-slate-500">{NO_KOREAN}</p></LangText>}
    </div>
  )
}

function TermsTab() {
  const [kind, setKind] = useState<TermsKind>('PARTNER')
  return (
    <>
      <div className="mb-4 flex gap-2" role="group" aria-label="Terms kind">
        {TERMS_KINDS.map((k) => (
          <Button key={k} tone={k === kind ? 'primary' : 'plain'} onClick={() => setKind(k)}>
            {TERMS_KIND_LABEL[k]}
          </Button>
        ))}
      </div>
      {/* ⚠ 종류를 바꾸면 창·목록을 새로 — 한 종류의 판 번호가 다른 종류 창구로 새지 않게. */}
      <TermsList key={kind} kind={kind} />
    </>
  )
}

function TermsList({ kind }: { kind: TermsKind }) {
  const { has } = useSession()
  const list = useQuery({ queryKey: ['partner-terms', kind], queryFn: () => listPartnerTerms(kind) })
  const effective = useQuery({ queryKey: ['partner-terms', kind, 'effective'], queryFn: () => effectiveOrNull(kind) })
  const [viewing, setViewing] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  // 판 새것부터.
  const rows = [...(list.data ?? [])].sort((a, b) => b.version - a.version)
  const current = effective.data?.version ?? null

  return (
    <>
      <p className="mb-3 text-xs text-slate-500">
        {TERMS_WHERE[kind]}
        {/* 2026-10-02 사실대로 — 확인 창 글은 거래소 서버가 주는 영어 한 줄(XA-67 `confirmText` — 사장님 확인 36 의 한국어 글을
            서버가 옮긴 것, 서버 03 52번 ⑧ 제안)이라 여기서 바꾸지 않는다. 2026-10-12 사실대로 — 시연용 한국어(서버 스위치
            켜짐·한국어 폰)에서는 서버 한국어 한 벌의 체크리스트 H1-22 비고 문장(거래소백엔드 bf604f4). */}
        {kind === 'WITHDRAWAL' && ' The confirmation dialog after the terms notice shows a fixed sentence from the exchange server (English, or Korean in the demo), so it is not changed here.'}
      </p>
      {has('terms.edit') && (
        <div className="mb-4"><Button onClick={() => setCreating(true)}>New version</Button></div>
      )}
      <QueryState isPending={list.isPending} error={list.error} empty={rows.length === 0}
        emptyText={`There are no ${TERMS_KIND_LABEL[kind].toLowerCase()}.`}>
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Version</th><th className={TH}>Title</th><th className={TH}>Korean title (demo)</th>
            <th className={TH}>Effective at</th><th className={TH}>Created by</th><th className={TH}>Created at</th>
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.version}>
                <td className={TD}>
                  <button type="button" className="text-teal-700 underline dark:text-teal-400"
                    onClick={() => setViewing(t.version)}>v{t.version}</button>
                  {current === t.version && <Badge tone="ok">In effect</Badge>}
                  {t.temporary && <Badge tone="warn">Temporary</Badge>}
                </td>
                <td className={TD}>{t.title}</td>
                <td className={TD}><Maybe value={hasKorean(t.titleKo, t.bodyKo) ? t.titleKo : null} /></td>
                <td className={TD}><Time ms={t.effectiveAt} /></td>
                <td className={TD}><AdminRef adminId={t.createdByAdminId} /></td>
                <td className={TD}><Time ms={t.createdAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      {viewing !== null && (
        <TermsViewDialog kind={kind} version={viewing} effective={current} onClose={() => setViewing(null)} />
      )}
      {creating && (
        <NewTermsDialog kind={kind} base={effective.data ?? null} onClose={() => setCreating(false)} />
      )}
    </>
  )
}

/** EA-52 — 약관 판 보기(XM-88). 본문은 글로만 — HTML 로 그리지 않는다. 2026-10-12: 영어 판과 한국어 판(시연용)을 나란히. */
function TermsViewDialog({ kind, version, effective, onClose }: {
  kind: TermsKind
  version: number
  effective: number | null
  onClose: () => void
}) {
  const q = useQuery({ queryKey: ['partner-terms', kind, version], queryFn: () => getPartnerTerms(version, kind) })
  const t = q.data
  const name = TERMS_KIND_LABEL[kind]
  return (
    <ViewDialog open title={t ? `${name} v${t.version} — ${t.title}` : `${name} v${version}`} onClose={onClose}>
      <QueryState isPending={q.isPending} error={q.error}>
        {t && (
          <>
            <dl className="mb-3 grid grid-cols-[6rem_1fr] gap-1.5">
              <Row label="Effective at">
                <Time ms={t.effectiveAt} />
                {effective === t.version && <Badge tone="ok">In effect</Badge>}
                {t.temporary && <Badge tone="warn">Temporary</Badge>}
              </Row>
              <Row label="Created by"><AdminRef adminId={t.createdByAdminId} /></Row>
              <Row label="Created at"><Time ms={t.createdAt} /></Row>
            </dl>
            <TermsSideBySide english="English" korean="Korean (demo)" title={t.title} body={t.body}
              titleKo={t.titleKo} bodyKo={t.bodyKo} />
          </>
        )}
      </QueryState>
    </ViewDialog>
  )
}

/**
 * EA-53 — 약관 새 판(XM-89). 처음 값은 시행 중 판(없으면 빈 칸 — 제안 03 6번). 2026-10-12: 영어 칸과 나란히 한국어(시연용)
 * 제목·본문 칸 — 둘 다 채우거나 둘 다 비운다(하나만이면 「Create」 잠김, 서버도 400), 「Preview」 — 앱에 보일 영어 판·한국어 판.
 */
function NewTermsDialog({ kind, base, onClose }: {
  kind: TermsKind
  base: PartnerTerms | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const w = useWrite()
  const [title, setTitle] = useState(base?.title ?? '')
  const [body, setBody] = useState(base?.body ?? '')
  const [titleKo, setTitleKo] = useState(base?.titleKo ?? '')
  const [bodyKo, setBodyKo] = useState(base?.bodyKo ?? '')
  const [effectiveAt, setEffectiveAt] = useState('')
  const [preview, setPreview] = useState(false)

  // 시행일은 「yyyy-mm-dd HH:mm:ss」(관리자 컴퓨터 시간)를 밀리초로(비우면 서버가 지금으로).
  const { ms: effectiveMs, bad: badDate } = effectiveMillis(effectiveAt)
  // 한국어는 둘 다 또는 둘 다 없음(서버 V544 와 같게 — 공백만이면 빈 칸).
  const korean = hasKorean(titleKo, bodyKo)
  const halfKorean = !korean && (titleKo.trim() !== '' || bodyKo.trim() !== '')

  async function submit() {
    const send = async () => {
      const t = await createPartnerTerms(title, body, effectiveMs, kind, korean ? { titleKo, bodyKo } : undefined)
      setFlash({ tone: 'ok', text: `Created ${TERMS_KIND_LABEL[kind]} v${t.version}.` })
      await qc.invalidateQueries({ queryKey: ['partner-terms'] })
    }
    await submitWrite(w, 'New terms version', send, onClose)
  }

  return (
    <Confirm open wide title={`${TERMS_KIND_LABEL[kind]} — new version`} action="Create" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={title.trim() === '' || body.trim() === '' || badDate || halfKorean}>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="flex flex-col gap-3">
            <Field label="Title (required · 200 chars)" value={title} maxLength={200}
              onChange={(e) => setTitle(e.target.value)} />
            <TextArea label="Body (required · 200,000 chars)" value={body} maxLength={200000} rows={12}
              onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="flex flex-col gap-3">
            <Field label="Korean title (demo · optional · 200 chars)" value={titleKo} maxLength={200}
              onChange={(e) => setTitleKo(e.target.value)} />
            <TextArea label="Korean body (demo · optional · 200,000 chars)" value={bodyKo} maxLength={200000} rows={12}
              onChange={(e) => setBodyKo(e.target.value)} />
          </div>
        </div>
        {halfKorean && <Notice tone="stop">{KOREAN_PAIR}</Notice>}
        <p className="text-xs text-slate-500">{KOREAN_NOTE}</p>
        <DateTimeField label="Effective at (optional)" name="Effective at" value={effectiveAt} onChange={setEffectiveAt} />
        {badDate && <Notice tone="stop">The effective time cannot be read.</Notice>}
        <div>
          <Button tone="plain" onClick={() => setPreview((p) => !p)}>{preview ? 'Hide preview' : 'Preview'}</Button>
        </div>
        {preview && (
          <section aria-label="Preview">
            <TermsSideBySide english="App in English (also the release)" korean="App in Korean (demo)"
              title={title.trim()} body={body} titleKo={titleKo} bodyKo={bodyKo} />
          </section>
        )}
        <p className="text-xs text-slate-500">
          Saves as a new version. Old versions are not deleted. {TERMS_WHERE[kind]}
        </p>
      </div>
    </Confirm>
  )
}
